import {Component, MeshComponent, Object3D, PhysXComponent} from '@wonderlandengine/api';
import {hapticFeedback} from '../utils/haptic-feedback.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {Cursor, CursorTarget} from '@wonderlandengine/components';
import {BoardBlock} from './board-block.js';
import {CursorReticle, CursorType} from './cursor-reticle.js';
import {PhongMaterial} from '../types/materials.js';

/**
 * board-interaction
 */
export class BoardInteraction extends Component {
    static TypeName = 'board-interaction';

    @property.object()
    boardBlock?: Object3D;
    @property.object()
    cursorReticleObject?: Object3D;
    @property.float(0.12)
    hoverHighlightStrength = 0.12;

    clickable = true;
    physxComponent?: PhysXComponent;
    boardBlockComponent?: BoardBlock;
    cursorReticleComponent?: CursorReticle;
    meshComponent?: MeshComponent;

    start() {
        /* Add Cursor Functions */
        const target =
            this.object.getComponent(CursorTarget) ||
            this.object.addComponent(CursorTarget);

        target?.onHover.add(this.onHover.bind(this));
        target?.onUnhover.add(this.onUnhover.bind(this));
        target?.onDown.add(this.onDown.bind(this));

        const tempPhysxComponent = this.object.getComponent(PhysXComponent);
        if (!tempPhysxComponent) {
            throw new Error(
                `board-interaction(${this.object.name}): object does not have a PhysXComponent`
            );
        }
        this.physxComponent = tempPhysxComponent;

        if (!this.boardBlock) {
            throw new Error(
                `board-interaction(${this.object.name}):boardBlock is not defined`
            );
        }

        const tempBoardBlockComponent = this.boardBlock.getComponent(BoardBlock);
        if (!tempBoardBlockComponent) {
            throw new Error(
                `board-interaction(${this.object.name}): object does not have a BoardBlock`
            );
        }
        this.boardBlockComponent = tempBoardBlockComponent;

        if (!this.cursorReticleObject) {
            throw new Error(
                `board-interaction(${this.object.name}).cursorReticleObject is not defined`
            );
        }

        const tempCursorReticleComponent =
            this.cursorReticleObject.getComponent(CursorReticle);
        if (!tempCursorReticleComponent) {
            throw new Error(
                `board-interaction(${this.object.name}): cursorReticleObject does not have a CursorReticle component`
            );
        }
        this.cursorReticleComponent = tempCursorReticleComponent;

        const tempMeshComponent = this.object.getComponent(MeshComponent);
        if (!tempMeshComponent) {
            throw new Error(
                `board-interaction(${this.object.name}): object does not have a MeshComponent`
            );
        }
        this.meshComponent = tempMeshComponent;

        /* Gate */
        this.clickable = true;
    }

    onHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.5, 50);

        const material = this.meshComponent!.material! as PhongMaterial;
        material.emissiveColor = [
            this.hoverHighlightStrength,
            this.hoverHighlightStrength,
            this.hoverHighlightStrength,
            1,
        ];
        this.cursorReticleComponent!.changeCursorTexture(cursor, CursorType.Pointer);
    }

    onUnhover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.3, 50);
        const material = this.meshComponent!.material! as PhongMaterial;
        material.emissiveColor = [0, 0, 0, 1];
        this.cursorReticleComponent!.changeCursorTexture(cursor, CursorType.Default);
    }

    onDown(_: Object3D, cursor: Cursor) {
        if (!this.clickable) {
            return;
        }
        this.clickable = false;

        /* Board Fall */
        this.physxComponent!.kinematic = false;
        this.physxComponent!.gravity = true;
        this.physxComponent!.addTorque([-100, -80, 100]);
        this.physxComponent!.blocksMask = 0b11101111; //239; /* Unblock Player */

        hapticFeedback(cursor.object, 1.0, 20);

        /* Boardblock destroy when all boards fallen */
        this.boardBlockComponent!.boardFall();

        this.active = false;
    }
}
